import React, { useState } from 'react';

import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Divider,
} from '@mui/material';
import clsx from 'clsx';
import CustomLink from 'components/CustomLink';
import Tooltip from 'components/Tooltip';
import VerifyAccessLevel from 'components/VerifyAccessLevel';
import { adminTypes } from 'helper/constant';
import PropTypes from 'prop-types';
import SignDocument from 'views/V2/Home/components/SignDocument';

import styles from './setupAccordion.module.css';
import useStyles from './styles';

import { ReactComponent as CheckIconCircle } from 'asset/IconsPack/at_check-circle.svg';
import { ReactComponent as CheckIcon } from 'asset/IconsPack/at_check.svg';
import { ReactComponent as ExpandIcon } from 'asset/IconsPack/at_chevron-down-filled.svg';

import SetupProgressbar from '../SetupProgressbar';

const { COMPANY_OWNER, COMPANY_ADMIN } = adminTypes;

const SetupAccordion = ({ setupData, onButtonClick }) => {
    const classes = useStyles();

    let totalSteps = 0;

    let completedSteps = 0;

    setupData.steps.forEach((step) => {
        // there is a step that doesn't have a completed property which treated as optional step
        if (Object?.hasOwn(step, 'completed')) {
            totalSteps++;
        }

        if (step?.completed) {
            completedSteps++;
        }
    });

    const isStepCompleted = completedSteps === totalSteps;

    const [isExpanded, setIsExpanded] = useState(!isStepCompleted); // Closed if all steps completed, open otherwise
    const [isSubAccordionExpanded, setIsSubAccordionExpanded] = useState({});

    const handleAccordionChange = (event, expanded) => {
        setIsExpanded(expanded);
    };

    const handleSubAccordionChange = (event, expanded, title) => {
        const newState = { ...isSubAccordionExpanded };

        newState[title] = expanded;

        setIsSubAccordionExpanded(newState);
    };

    const MainHeaderIcon = setupData.Icon;

    return (
        <Box className={classes.accordionContainer}>
            <>
                <Accordion
                    className={classes.accordionRoot}
                    key={setupData.id}
                    expanded={isExpanded}
                    onChange={handleAccordionChange}
                >
                    <AccordionSummary expandIcon={<ExpandIcon className={classes.expandIcon} />}>
                        {/* Parent accordion header */}
                        <Box className={classes.mainAccordionHeader}>
                            <Typography className={classes.mainAccordionHeaderTitle}>
                                <MainHeaderIcon className={classes.mainHeaderIcon} />{' '}
                                {setupData.title}
                            </Typography>
                            {!isExpanded && isStepCompleted ? (
                                <Typography className={classes.subTitleCompleted}>
                                    Completed{' '}
                                    <CheckIcon
                                        className={clsx(classes.mainSubHeaderIcon, classes.success)}
                                    />
                                </Typography>
                            ) : (
                                <Typography className={classes.mainAccordionHeaderSubtitle}>
                                    {setupData.duration}
                                </Typography>
                            )}
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                        {setupData?.steps?.map(({ Icon, ...step }, index) => (
                            <React.Fragment key={step.id}>
                                <Divider className={classes.divider} />

                                {step.title === 'Review Agreement' ? (
                                    <VerifyAccessLevel
                                        allowedAdmins={[COMPANY_OWNER, COMPANY_ADMIN]}
                                        hideContentWhenInActive={false}
                                    >
                                        <SignDocument
                                            classes={classes}
                                            totalSteps={totalSteps}
                                            handleSubAccordionChange={(event, expand) =>
                                                handleSubAccordionChange(event, expand, step.title)
                                            }
                                            isSubAccordionExpanded={
                                                isSubAccordionExpanded[step.title] ?? false
                                            }
                                        />
                                    </VerifyAccessLevel>
                                ) : (
                                    <Accordion
                                        className={classes.subAccordionRoot}
                                        expanded={isSubAccordionExpanded[step.title] ?? false}
                                        onChange={(event, expand) =>
                                            handleSubAccordionChange(event, expand, step.title)
                                        }
                                    >
                                        <AccordionSummary
                                            className={classes.subAccordionSummary}
                                            expandIcon={
                                                <ExpandIcon className={classes.expandIcon} />
                                            }
                                        >
                                            {/* Sub accordion header container */}
                                            <Box className={classes.subAccordionHeader}>
                                                <Typography
                                                    className={classes.subAccordionHeaderTitle}
                                                >
                                                    {step?.completed ? (
                                                        <CheckIconCircle
                                                            className={clsx(
                                                                classes.mainSubHeaderIcon,
                                                                classes.success
                                                            )}
                                                        />
                                                    ) : (
                                                        <Icon
                                                            className={classes.mainSubHeaderIcon}
                                                        />
                                                    )}{' '}
                                                    {step.title}
                                                </Typography>
                                                {step?.button && !step.completed && (
                                                    <Tooltip
                                                        title={
                                                            step?.button?.disabled
                                                                ? step?.button?.tooltip ?? ''
                                                                : ''
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <span>
                                                            <CustomLink
                                                                link={step?.button?.link}
                                                                variant="outlined"
                                                                linkText={step?.button?.text}
                                                                customLinkStyle={
                                                                    styles.subAccordionLink
                                                                }
                                                                customLinkTextStyle={
                                                                    styles.subAccordionLinkText
                                                                }
                                                                disabled={step?.button?.disabled}
                                                                external={step?.button?.external}
                                                                isOnClickHandler={
                                                                    step?.button?.isOnClickHandler
                                                                }
                                                                onClick={(event) =>
                                                                    onButtonClick?.(event, step)
                                                                }
                                                            />
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            <Typography
                                                className={classes.mainAccordionHeaderSubtitle}
                                            >
                                                Step {index + 1} of {setupData.steps.length}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails className={classes.subAccordionDetails}>
                                            {/* Sub accordion body container */}
                                            <Box className={classes.subAccordionDetailsContainer}>
                                                <Typography
                                                    className={classes.subAccordionDetailsText}
                                                    style={{ whiteSpace: 'pre-line' }}
                                                >
                                                    {step.content}
                                                </Typography>
                                                {step?.subContent && (
                                                    <Box
                                                        className={
                                                            classes.subAccordionDetailsSubContent
                                                        }
                                                    >
                                                        {step?.subContent?.map((subContent) => (
                                                            <Box
                                                                key={subContent.id}
                                                                className={classes.subContentItem}
                                                            >
                                                                <Typography
                                                                    className={
                                                                        classes.subContentText
                                                                    }
                                                                >
                                                                    {subContent.content}
                                                                </Typography>
                                                                {subContent?.button && (
                                                                    <CustomLink
                                                                        link={
                                                                            subContent?.button?.link
                                                                        }
                                                                        variant="outlined"
                                                                        linkText={
                                                                            subContent?.button?.text
                                                                        }
                                                                        customLinkStyle={
                                                                            styles.subAccordionLink
                                                                        }
                                                                        customLinkTextStyle={
                                                                            styles.subAccordionLinkText
                                                                        }
                                                                        disabled={false}
                                                                        external={
                                                                            subContent?.button
                                                                                ?.external
                                                                        }
                                                                    />
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </React.Fragment>
                        ))}
                    </AccordionDetails>
                </Accordion>
                <SetupProgressbar completedSteps={completedSteps} totalSteps={totalSteps} />
            </>
        </Box>
    );
};

SetupAccordion.propTypes = {
    setupData: PropTypes.object.isRequired,
    onButtonClick: PropTypes.func,
};

export default SetupAccordion;
